import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '../components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { reportsAPI, inventoryAPI, adminAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import {
    DollarSign, ShoppingCart, Package, AlertTriangle,
    TrendingUp, ArrowUpRight, ArrowRight, Plus,
    Store, Users, CheckCircle, Clock, Shield, RefreshCw
} from 'lucide-react';
import {
    AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
    ResponsiveContainer
} from 'recharts';

const PLAN_MRR = { starter: 29, professional: 79, enterprise: 149 };

const HEALTH_DOT = {
    active: 'bg-emerald-500',
    trial: 'bg-cyan-400',
    idle: 'bg-amber-400',
    expired: 'bg-red-500',
    suspended: 'bg-red-600',
    disconnected: 'bg-slate-500',
    setup: 'bg-violet-400',
};

// ─────────────────────────────────────────────────────────────────
// Super Admin Platform Overview
// ─────────────────────────────────────────────────────────────────
function SuperAdminDashboard() {
    const [stores, setStores] = useState([]);
    const [loading, setLoading] = useState(true);

    const fetchStores = async () => {
        setLoading(true);
        try {
            const res = await adminAPI.listStores();
            setStores(res.data?.stores || []);
        } catch {
            toast.error('Failed to load platform data');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchStores(); }, []);

    const total = stores.length;
    const active = stores.filter(s => s.subscription_status === 'active').length;
    const onTrial = stores.filter(s => s.subscription_status === 'trial').length;
    const mrr = stores
        .filter(s => s.subscription_status === 'active')
        .reduce((sum, s) => sum + (PLAN_MRR[s.subscription_plan] || 0), 0);

    const statCards = [
        { label: 'Total Subscribers', value: total, icon: Store, accent: 'border-l-violet-500', iconBg: 'bg-violet-500/10', iconColor: 'text-violet-400' },
        { label: 'Active', value: active, icon: CheckCircle, accent: 'border-l-emerald-500', iconBg: 'bg-emerald-500/10', iconColor: 'text-emerald-400' },
        { label: 'On Trial', value: onTrial, icon: Clock, accent: 'border-l-cyan-500', iconBg: 'bg-cyan-500/10', iconColor: 'text-cyan-400' },
        { label: 'Monthly Revenue', value: `$${mrr}`, icon: DollarSign, accent: 'border-l-amber-500', iconBg: 'bg-amber-500/10', iconColor: 'text-amber-400' },
    ];

    if (loading) {
        return (
            <Layout title="Platform Overview">
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                    {[...Array(4)].map((_, i) => <div key={i} className="h-28 rounded-xl bg-muted animate-pulse" />)}
                </div>
                <div className="h-64 rounded-xl bg-muted animate-pulse" />
            </Layout>
        );
    }

    return (
        <Layout title="Platform Overview">
            <div className="space-y-6">

                {/* Header actions */}
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-lg bg-violet-500/20 flex items-center justify-center">
                            <Shield className="h-4 w-4 text-violet-400" />
                        </div>
                        <div>
                            <p className="font-bold text-sm">Mezgeb POS — Operator Console</p>
                            <p className="text-xs text-muted-foreground">Real-time platform snapshot</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <Button variant="outline" size="sm" onClick={fetchStores} className="gap-1.5 text-xs">
                            <RefreshCw className="h-3 w-3" /> Refresh
                        </Button>
                        <Link to="/admin">
                            <Button size="sm" className="bg-violet-600 hover:bg-violet-700 text-white text-xs gap-1.5">
                                <Store className="h-3 w-3" /> Manage Subscribers
                            </Button>
                        </Link>
                    </div>
                </div>

                {/* Stat Cards */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    {statCards.map((c) => {
                        const Icon = c.icon;
                        return (
                            <div key={c.label} className={`stat-card border-l-4 ${c.accent}`}>
                                <div className="flex items-start justify-between mb-3">
                                    <div className={`stat-card-icon ${c.iconBg}`}>
                                        <Icon className={`h-5 w-5 ${c.iconColor}`} />
                                    </div>
                                    <ArrowUpRight className="h-4 w-4 text-muted-foreground" />
                                </div>
                                <div className="text-3xl font-black font-mono tracking-tight">{c.value}</div>
                                <p className="text-xs text-muted-foreground mt-1 font-medium">{c.label}</p>
                            </div>
                        );
                    })}
                </div>

                {/* Subscriber List */}
                <Card className="shadow-sm">
                    <CardHeader className="pb-3">
                        <div className="flex items-center justify-between">
                            <CardTitle className="flex items-center gap-2 text-base">
                                <Users className="h-4 w-4 text-violet-400" />
                                Subscribers
                            </CardTitle>
                            <Link to="/admin">
                                <Button variant="ghost" size="sm" className="text-xs text-muted-foreground gap-1">
                                    View all <ArrowRight className="h-3 w-3" />
                                </Button>
                            </Link>
                        </div>
                    </CardHeader>
                    <CardContent className="p-0">
                        {stores.length === 0 ? (
                            <div className="py-12 text-center text-sm text-muted-foreground">
                                No subscribers yet. Share mezgebpos.com to get started.
                            </div>
                        ) : (
                            <div className="divide-y divide-border">
                                {stores.slice(0, 8).map((store) => (
                                    <div key={store.id} className="flex items-center gap-4 px-5 py-3.5 hover:bg-muted/30 transition-colors">
                                        <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${HEALTH_DOT[store.system_health] || 'bg-slate-500'}`} />
                                        <div className="flex-1 min-w-0">
                                            <p className="font-semibold text-sm truncate">{store.name}</p>
                                            <p className="text-xs text-muted-foreground truncate">{store.owner_email}</p>
                                        </div>
                                        <Badge variant="outline" className="text-[10px] capitalize shrink-0">
                                            {store.subscription_plan || 'trial'}
                                        </Badge>
                                        <Badge className={`text-[10px] capitalize border-0 shrink-0 ${
                                            store.subscription_status === 'active' ? 'bg-emerald-500/15 text-emerald-400' :
                                            store.subscription_status === 'trial'  ? 'bg-cyan-500/15 text-cyan-400' :
                                            store.subscription_status === 'idle'   ? 'bg-amber-500/15 text-amber-400' :
                                            'bg-red-500/15 text-red-400'
                                        }`}>
                                            {store.subscription_status}
                                        </Badge>
                                        <div className="text-right shrink-0">
                                            <p className="text-xs font-mono font-semibold">{store.products_count ?? '—'} products</p>
                                            <p className="text-[10px] text-muted-foreground">{store.users_count ?? '—'} users</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* Plan Breakdown */}
                {stores.length > 0 && (
                    <div className="grid grid-cols-3 gap-4">
                        {Object.entries(PLAN_MRR).map(([plan, price]) => {
                            const count = stores.filter(s => s.subscription_plan === plan && s.subscription_status === 'active').length;
                            return (
                                <Card key={plan} className="shadow-sm">
                                    <CardContent className="p-4">
                                        <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground capitalize mb-2">{plan}</p>
                                        <p className="text-2xl font-black font-mono">{count}</p>
                                        <p className="text-xs text-muted-foreground mt-1">${price}/mo · <span className="font-semibold text-foreground">${count * price} MRR</span></p>
                                    </CardContent>
                                </Card>
                            );
                        })}
                    </div>
                )}
            </div>
        </Layout>
    );
}

// ─────────────────────────────────────────────────────────────────
// Store-level Dashboard (unchanged)
// ─────────────────────────────────────────────────────────────────
const STAT_CONFIGS = [
    {
        key: 'today', title: "Today's Revenue", icon: DollarSign,
        iconBg: 'bg-emerald-500/10', iconColor: 'text-emerald-600 dark:text-emerald-400',
        valueFn: (s) => `$${(s?.today_revenue || 0).toFixed(2)}`,
        descFn: (s) => `${s?.today_transactions || 0} transactions today`,
        accent: 'border-l-4 border-l-emerald-500'
    },
    {
        key: 'total', title: 'Total Revenue', icon: TrendingUp,
        iconBg: 'bg-cyan-500/10', iconColor: 'text-cyan-600 dark:text-cyan-400',
        valueFn: (s) => `$${(s?.total_revenue || 0).toFixed(2)}`,
        descFn: (s) => `${s?.total_transactions || 0} all-time sales`,
        accent: 'border-l-4 border-l-cyan-500'
    },
    {
        key: 'products', title: 'Products', icon: Package,
        iconBg: 'bg-violet-500/10', iconColor: 'text-violet-600 dark:text-violet-400',
        valueFn: (s) => s?.total_products || 0,
        descFn: (s) => `${s?.total_categories || 0} categories`,
        accent: 'border-l-4 border-l-violet-500'
    },
    {
        key: 'lowstock', title: 'Low Stock', icon: AlertTriangle,
        iconBg: 'bg-amber-500/10', iconColor: 'text-amber-600 dark:text-amber-400',
        valueFn: (s) => s?.low_stock_count || 0,
        descFn: () => 'Items need restock',
        accent: 'border-l-4 border-l-amber-500'
    }
];

function StoreDashboard() {
    const [stats, setStats] = useState(null);
    const [salesData, setSalesData] = useState([]);
    const [topProducts, setTopProducts] = useState([]);
    const [lowStockProducts, setLowStockProducts] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [dashboardRes, salesRes, topRes, lowStockRes] = await Promise.all([
                    reportsAPI.getDashboard(),
                    reportsAPI.getSalesByDate(7),
                    reportsAPI.getTopProducts(5),
                    inventoryAPI.getLowStock()
                ]);
                setStats(dashboardRes.data || {});
                setSalesData(salesRes.data || []);
                setTopProducts(topRes.data || []);
                setLowStockProducts(lowStockRes.data || []);
            } catch {
                setStats({}); setSalesData([]); setTopProducts([]); setLowStockProducts([]);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, []);

    if (loading) {
        return (
            <Layout title="Dashboard">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                    {[...Array(4)].map((_, i) => <div key={i} className="h-28 rounded-xl bg-muted animate-pulse" />)}
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div className="h-80 rounded-xl bg-muted animate-pulse" />
                    <div className="h-80 rounded-xl bg-muted animate-pulse" />
                </div>
            </Layout>
        );
    }

    return (
        <Layout title="Dashboard">
            <div className="space-y-6" data-testid="dashboard-page">
                <div className="flex items-center gap-3">
                    <Link to="/pos">
                        <Button className="bg-cyan-500 hover:bg-cyan-600 text-white font-semibold shadow-sm shadow-cyan-500/20 gap-2">
                            <ShoppingCart className="h-4 w-4" /> New Sale
                        </Button>
                    </Link>
                    <Link to="/products">
                        <Button variant="outline" className="gap-2">
                            <Plus className="h-4 w-4" /> Add Product
                        </Button>
                    </Link>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    {STAT_CONFIGS.map((cfg) => {
                        const Icon = cfg.icon;
                        return (
                            <div key={cfg.key} className={`stat-card card-hover ${cfg.accent}`} data-testid={`stat-card-${cfg.key}`}>
                                <div className="flex items-start justify-between mb-3">
                                    <div className={`stat-card-icon ${cfg.iconBg}`}><Icon className={`h-5 w-5 ${cfg.iconColor}`} /></div>
                                    <ArrowUpRight className="h-4 w-4 text-muted-foreground" />
                                </div>
                                <div className="text-3xl font-black font-mono tracking-tight text-foreground">{cfg.valueFn(stats)}</div>
                                <p className="text-sm font-semibold text-foreground mt-1">{cfg.title}</p>
                                <p className="text-xs text-muted-foreground mt-0.5">{cfg.descFn(stats)}</p>
                            </div>
                        );
                    })}
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    <Card className="lg:col-span-2 shadow-sm" data-testid="sales-chart">
                        <CardHeader className="pb-2">
                            <div className="flex items-center justify-between">
                                <CardTitle className="flex items-center gap-2 text-base">
                                    <TrendingUp className="h-4 w-4 text-cyan-500" /> Revenue (Last 7 Days)
                                </CardTitle>
                                <Link to="/reports">
                                    <Button variant="ghost" size="sm" className="text-xs text-muted-foreground gap-1">
                                        Full report <ArrowRight className="h-3 w-3" />
                                    </Button>
                                </Link>
                            </div>
                        </CardHeader>
                        <CardContent>
                            <div className="h-[260px]">
                                <ResponsiveContainer width="100%" height="100%">
                                    <AreaChart data={salesData} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
                                        <defs>
                                            <linearGradient id="revenueGrad" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="5%" stopColor="#06b6d4" stopOpacity={0.25} />
                                                <stop offset="95%" stopColor="#06b6d4" stopOpacity={0} />
                                            </linearGradient>
                                        </defs>
                                        <CartesianGrid strokeDasharray="3 3" className="stroke-border" vertical={false} />
                                        <XAxis dataKey="date" tickFormatter={(v) => new Date(v).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
                                        <YAxis tickFormatter={(v) => `$${v}`} tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
                                        <Tooltip formatter={(v) => [`$${v.toFixed(2)}`, 'Revenue']} labelFormatter={(v) => new Date(v).toLocaleDateString()} contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '10px', fontSize: '12px' }} />
                                        <Area type="monotone" dataKey="revenue" stroke="#06b6d4" strokeWidth={2.5} fill="url(#revenueGrad)" dot={false} />
                                    </AreaChart>
                                </ResponsiveContainer>
                            </div>
                        </CardContent>
                    </Card>

                    <Card className="shadow-sm" data-testid="top-products-chart">
                        <CardHeader className="pb-2">
                            <CardTitle className="flex items-center gap-2 text-base">
                                <ShoppingCart className="h-4 w-4 text-violet-500" /> Top Products
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="space-y-3">
                                {topProducts.length === 0 ? (
                                    <p className="text-sm text-muted-foreground text-center py-8">No sales data yet</p>
                                ) : topProducts.slice(0, 5).map((p, i) => (
                                    <div key={i} className="flex items-center gap-3">
                                        <div className="w-6 h-6 rounded-md bg-violet-500/10 flex items-center justify-center shrink-0">
                                            <span className="text-[10px] font-bold text-violet-600 dark:text-violet-400">{i + 1}</span>
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-medium truncate">{p.product_name}</p>
                                            <div className="h-1.5 bg-muted rounded-full mt-1">
                                                <div className="h-full bg-violet-500 rounded-full" style={{ width: `${Math.round((p.revenue / topProducts[0].revenue) * 100)}%` }} />
                                            </div>
                                        </div>
                                        <span className="text-sm font-mono font-semibold shrink-0">${p.revenue?.toFixed(0)}</span>
                                    </div>
                                ))}
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {lowStockProducts.length > 0 && (
                    <Card className="border-amber-500/30 shadow-sm" data-testid="low-stock-alert">
                        <CardHeader className="pb-3">
                            <div className="flex items-center justify-between">
                                <CardTitle className="flex items-center gap-2 text-base text-amber-600 dark:text-amber-400">
                                    <AlertTriangle className="h-4 w-4" />
                                    Low Stock Alert
                                    <Badge variant="secondary" className="bg-amber-500/10 text-amber-600 dark:text-amber-400 border-0 text-xs">{lowStockProducts.length} items</Badge>
                                </CardTitle>
                                <Link to="/inventory">
                                    <Button variant="ghost" size="sm" className="text-xs text-muted-foreground gap-1">Manage <ArrowRight className="h-3 w-3" /></Button>
                                </Link>
                            </div>
                        </CardHeader>
                        <CardContent>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                                {lowStockProducts.slice(0, 6).map((product) => (
                                    <div key={product.id} className="flex items-center justify-between p-3 rounded-lg bg-amber-500/5 border border-amber-500/20" data-testid={`low-stock-item-${product.id}`}>
                                        <div className="min-w-0">
                                            <p className="font-semibold text-sm truncate">{product.name}</p>
                                            <p className="text-xs text-muted-foreground font-mono">{product.sku}</p>
                                        </div>
                                        <div className="shrink-0 ml-3 text-right">
                                            <p className="text-lg font-black text-amber-600 dark:text-amber-400 font-mono">{product.stock_quantity}</p>
                                            <p className="text-[10px] text-muted-foreground">in stock</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </CardContent>
                    </Card>
                )}
            </div>
        </Layout>
    );
}

// ─────────────────────────────────────────────────────────────────
// Entry point — route to correct view
// ─────────────────────────────────────────────────────────────────
export default function Dashboard() {
    const { user } = useAuth();
    const isSuperAdmin = user?.role === 'admin' && !user?.store_id;
    return isSuperAdmin ? <SuperAdminDashboard /> : <StoreDashboard />;
}
